import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import type { IOrderSupplier } from '@d-orca/shared/client/hooks';
import type { ISupplierDraft } from '@d-orca/shared/client/logistics';
import styles from './SupplierField.module.css';

interface ISupplierMiniFormProps {
  supplier: IOrderSupplier;
  /** The open draft (`supplier.draft`, narrowed by the host). */
  draft: ISupplierDraft;
}

/**
 * A new or edited supplier's fields, inline in the order form (Company
 * required, Contact and Phone optional). Nothing is saved here: the order's
 * Save writes them. A refusal of the supplier shows on its field with
 * everything typed kept; Cancel discards the draft.
 */
export function SupplierMiniForm({ supplier, draft }: ISupplierMiniFormProps) {
  const { t } = useTranslation();
  const { error, setField } = supplier;
  const id = useId();

  return (
    <div className={styles.mini}>
      {/* Its own step of the form: what this box writes, said at its top. */}
      <span className={styles.miniTitle} role="heading" aria-level={4}>
        {t(draft.mode === 'edit' ? 'logistics.orderDialog.supplierDraftEdit' : 'logistics.orderDialog.supplierDraftNew')}
      </span>
      <div className={styles.field}>
        <label className={styles.label} htmlFor={`${id}-name`}>{t('logistics.orderDialog.supplierCompany')}</label>
        <input
          id={`${id}-name`}
          className={styles.input}
          value={draft.name}
          maxLength={80}
          aria-invalid={error === 'taken' || undefined}
          autoComplete="off"
          onChange={(e) => setField('name', e.target.value)}
        />
        {error === 'taken' ? <span className={styles.error}>{t('logistics.orderDialog.supplierNameTaken')}</span> : null}
      </div>
      <div className={styles.pair}>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${id}-contact`}>{t('logistics.orderDialog.supplierContact')}</label>
          <input
            id={`${id}-contact`}
            className={styles.input}
            value={draft.contactName}
            maxLength={60}
            placeholder={t('logistics.orderDialog.supplierContactPlaceholder')}
            autoComplete="off"
            onChange={(e) => setField('contactName', e.target.value)}
          />
        </div>
        <div className={styles.field}>
          <label className={styles.label} htmlFor={`${id}-phone`}>{t('logistics.orderDialog.supplierPhone')}</label>
          <input
            id={`${id}-phone`}
            type="tel"
            className={styles.input}
            value={draft.phone}
            maxLength={30}
            aria-invalid={error === 'phone' || undefined}
            autoComplete="off"
            onChange={(e) => setField('phone', e.target.value)}
          />
          {error === 'phone' ? <span className={styles.error}>{t('logistics.orderDialog.supplierPhoneInvalid')}</span> : null}
        </div>
      </div>
      {draft.mode === 'edit' ? <span className={styles.hint}>{t('logistics.orderDialog.supplierEditsEverywhere')}</span> : null}
      <button type="button" className={styles.textLink} onClick={supplier.cancel}>
        {t('logistics.orderDialog.cancel')}
      </button>
    </div>
  );
}
